/**
 * TemplateLibrary — standalone page wrapping TemplateLibraryBrowser.
 *
 * Route: /templates
 * Surface for managers and Bryan to browse the entire template catalog.
 * "Pick" copies the rendered subject + body to clipboard and shows a toast.
 *
 * Palette: Mi6 Black #0B0B0F · Red #DC2626 · AIP bg #FAFAF7.
 */
import { useState } from "react";
import TemplateLibraryBrowser from "@/components/templates/TemplateLibraryBrowser";
import { type TemplateScript } from "@/lib/template-library";
import OperatorShell from "@/components/operator/OperatorShell";
import { useToast } from "@/hooks/use-toast";

const MI6_BLACK = "#0B0B0F";
const MI6_RED = "#DC2626";

async function copyTemplate(script: TemplateScript): Promise<void> {
  const text = [
    script.subject ? `Subject: ${script.subject}` : "",
    script.body,
  ]
    .filter(Boolean)
    .join("\n\n");
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    // Fallback for non-secure contexts
    const ta = document.createElement("textarea");
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    try {
      document.execCommand("copy");
    } catch {
      // best-effort
    }
    document.body.removeChild(ta);
  }
}

export default function TemplateLibrary() {
  const [open, setOpen] = useState(true);
  const { toast } = useToast();

  // Primary action: "Use this template" — copies and shows confirmation
  function handlePick(script: TemplateScript) {
    copyTemplate(script).then(() => {
      toast({
        title: "Template ready",
        description: `"${script.title}" copied to clipboard. Paste into your compose window.`,
      });
      // Re-open the browser so users can keep browsing
      setTimeout(() => setOpen(true), 50);
    });
  }

  // Secondary action: "Copy to clipboard" — same behavior, explicit copy label
  function handleCopy(script: TemplateScript) {
    copyTemplate(script).then(() => {
      toast({
        title: "Template copied",
        description: `"${script.title}" copied to clipboard.`,
      });
    });
  }

  return (
    <OperatorShell
      breadcrumbs={[{ label: "Home", href: "/" }, { label: "Template Library" }]}
    >
      <div className="max-w-[1400px] w-full mx-auto">
        <div
          style={{
            background: "#FFFFFF",
            border: "1px solid #E5E5E7",
            borderRadius: 12,
            padding: 24,
          }}
        >
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div
                className="text-[9px] uppercase tracking-[0.22em] font-bold mb-1"
                style={{ color: MI6_RED }}
              >
                Library · Cadence Scripts
              </div>
              <h1
                style={{
                  fontSize: 22,
                  fontWeight: 900,
                  color: MI6_BLACK,
                  margin: 0,
                }}
              >
                Template Library
              </h1>
              <p
                className="text-sm mt-2"
                style={{ color: "#525258", maxWidth: 640 }}
              >
                The complete catalog of cadence scripts: email openers, follow-ups,
                LinkedIn DMs, phone scripts, and voicemails. Filter by channel and
                persona. Click any card to preview, then copy to clipboard for
                ad-hoc use, or trigger from a cadence step or compose modal.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="px-4 py-2 rounded-lg text-sm font-bold uppercase tracking-wider text-white shadow-sm transition-all hover:shadow-md whitespace-nowrap"
              style={{ background: MI6_BLACK }}
            >
              Open Library
            </button>
          </div>
        </div>
      </div>

      <TemplateLibraryBrowser
        open={open}
        onClose={() => setOpen(false)}
        onPick={handlePick}
        onCopy={handleCopy}
        title="Template Library"
      />
    </OperatorShell>
  );
}
